// @ts-check
import { insforge, INSFORGE_ANON_KEY, INSFORGE_URL } from "../insforge";

/**
 * Edge-function transport.
 *
 * We do NOT use the SDK's `functions.invoke()`. SDK 1.2.4 derives a dedicated
 * host for functions — `https://{appKey}.functions.insforge.app` — and that
 * host stopped existing when Insforge migrated off Deno Deploy Classic
 * (sunset 2026-07-20); the replacement is `{appKey}.function2.insforge.app`.
 * Every edge-function call from the browser therefore failed with a network
 * error, while the backend logged nothing at all because the requests never
 * reached it. Symptom: forms that "don't work" with no trace server-side.
 *
 * `{baseUrl}/functions/{slug}` is served by the API host, is what SDK 1.5.2+
 * uses, and is host-derivation-free — so it survives the next migration too.
 * Upgrading the SDK is not currently an option: 1.5.2 is the first release
 * with the new host and it imports node `crypto`, which CRA 5's webpack
 * refuses to bundle.
 *
 * Returns the SDK's `{ data, error }` shape so call sites are unchanged.
 *
 * @param {string} slug
 * @param {{ body?: any, method?: string }} [options]
 * @returns {Promise<{ data: any, error: any }>}
 */
export async function invokeFunction(slug, options = {}) {
  const { body, method = "POST" } = options;
  /** @type {Record<string, string>} */
  const headers = { "Content-Type": "application/json" };

  // Authenticated functions read the caller from the JWT; anonymous ones
  // (guest checkout, ticket recovery, the demo form) still need a key for
  // the gateway, so fall back to the anon key.
  let token = null;
  try {
    token = insforge?.auth?.getAccessToken?.() ?? null;
  } catch {
    token = null;
  }
  headers.Authorization = `Bearer ${token || INSFORGE_ANON_KEY}`;

  try {
    const res = await fetch(
      `${INSFORGE_URL.replace(/\/+$/, "")}/functions/${slug}`,
      {
        method,
        headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      }
    );
    const text = await res.text();
    let parsed = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = text || null;
    }
    if (!res.ok) {
      return {
        data: null,
        error: Object.assign(
          new Error(
            (parsed && (parsed.message || parsed.error)) ||
              `Function ${slug} failed (${res.status})`
          ),
          { statusCode: res.status, details: parsed }
        ),
      };
    }
    return { data: parsed, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error(String(err)),
    };
  }
}

/**
 * Edge-functions contract for the event-ticketing MVP.
 *
 * @typedef {{ data: any, error: any }} FunctionsResult
 *
 * @typedef {Object} FunctionsSurface
 * @property {(args: { ticketTypeId: string, quantity: number, buyerEmailHint?: string }) => Promise<FunctionsResult>} createTicketCheckout
 *   Creates a Stripe Checkout session for a multi-ticket purchase of a
 *   single ticket tier. Buyer is inferred from the JWT when present;
 *   guests can pass an email hint that Stripe will pre-fill.
 * @property {(args: { returnUrl: string, refreshUrl: string }) => Promise<FunctionsResult>} connectOnboard
 *   Creates or refreshes a Stripe Connect Express onboarding link.
 * @property {(args: { token: string }) => Promise<FunctionsResult>} getTicketsByToken
 *   Public, no-auth call backing the guest `/recover/:token` page. Returns
 *   the tickets tied to the buyer email referenced by `token`.
 * @property {(args: { email: string }) => Promise<FunctionsResult>} requestTicketRecovery
 *   Public, no-auth call to (re)send the recovery email for the given
 *   buyer email. Implemented by issue #15; this client method is added
 *   ahead of the edge function so the recovery page can wire its
 *   "resend" form without a follow-up PR. When #15 lands the handler
 *   must use the shared `rateLimit` helper at
 *   `insforge/functions/_rate-limit/index.ts` keyed on
 *   `recovery:email:<email>` (3/hour, per issue #111).
 * @property {(args: { css: string, eventId: string }) => Promise<FunctionsResult>} validateBrandCss
 *   Server-side validator/sanitizer for `events.brand_custom_css`. The
 *   branding editor posts the textarea contents here before saving so the
 *   organizer sees rejected directives + warnings instead of a silently
 *   broken stylesheet. Implementation: `insforge/functions/validate-brand-css`.
 * @property {(args: { ticketId: string, reason?: string, recoveryToken?: string }) => Promise<FunctionsResult>} refundTicket
 *   Per-ticket refund with two auth modes:
 *   - Organizer mode (default): JWT-authenticated organizer of the event.
 *   - Buyer self-service mode: pass `recoveryToken` (no JWT required); the
 *     edge function verifies the token against the ticket and enforces
 *     the event's `refund_cutoff_hours` policy. See issue #102.
 *   Refunds `purchase_amount_cents` against the original PaymentIntent
 *   (service fee is retained). The downstream `charge.refunded` webhook is
 *   the single writer that flips `ticket.status`, decrements `sold`, and
 *   emails the buyer.
 *   Implementation: `insforge/functions/refund-ticket`.
 * @property {(args: { email: string, type: 'access'|'rectification'|'erasure'|'portability', description?: string }) => Promise<FunctionsResult>} requestRgpd
 *   Public, no-auth call backing the `/legal/rgpd` form. Submits a GDPR
 *   rights request from a guest buyer, emails the DPO + the requester,
 *   and enforces a per-email/day rate limit.
 *   Implementation: `insforge/functions/rgpd-request`.
 * @property {() => Promise<FunctionsResult>} claimGuestTickets
 *   Authenticated call invoked from `useAuth` after a successful
 *   signup/login. Links every orphan ticket (`owner_id IS NULL`) whose
 *   `buyer_email` matches the user's verified email to their account.
 *   Returns `{ claimed_count }` so the UI can confirm how many tickets
 *   were attached. Idempotent.
 *   Implementation: `insforge/functions/claim-guest-tickets`.
 * @property {(args: { name: string, email: string, org?: string, message?: string }) => Promise<FunctionsResult>} requestDemo
 *   Public, no-auth call backing the landing "Solicitar una demo" form.
 *   Validates + rate-limits per email and emails the lead to
 *   `PIKRA_DEMO_EMAIL` (default jasansu@gmail.com) plus an acknowledgement
 *   to the requester, via the `send-email` (Resend) function.
 *   Implementation: `insforge/functions/request-demo`.
 * @property {(args: { eventId: string, subject: string, body: string }) => Promise<FunctionsResult>} sendEventNotification
 *   Organizer-initiated mass email to all attendees of an event. The
 *   handler dedups recipients by buyer_email (case-insensitive), sends
 *   the message in batches via `send-email`, audits one row per
 *   recipient in `event_notifications` (type `event_announcement`), and
 *   enforces a 1-send-per-hour rate limit per event. Refunded tickets
 *   are excluded from the recipient set.
 *   Implementation: `insforge/functions/send-event-notification`.
 * @property {(args: { slug: string, name: string, brandingJson?: object }) => Promise<FunctionsResult>} orgCreate
 *   Creates an organisation owned by the caller. The caller becomes the
 *   sole `owner` member. The slug must match `^[a-z0-9][a-z0-9_-]{1,79}$`
 *   and be unique within the active staging scope. See issue #194.
 *   Implementation: `insforge/functions/org-create`.
 * @property {(args: { organizationId: string, brandingJson: object }) => Promise<FunctionsResult>} orgUpdateBranding
 *   Updates an organisation's `branding_json` blob. Owners and editors
 *   can call this; door_only members cannot.
 *   Implementation: `insforge/functions/org-update-branding`.
 * @property {(args: { organizationId: string, email: string, role: 'owner'|'editor'|'door_only' }) => Promise<FunctionsResult>} orgAddMember
 *   Owner-only. Adds (or re-roles) a user identified by email.
 *   Implementation: `insforge/functions/org-add-member`.
 * @property {(args: { organizationId: string, userId: string }) => Promise<FunctionsResult>} orgRemoveMember
 *   Removes a member. Owners can remove anyone except the last remaining
 *   owner; any member can remove themselves under the same guard.
 *   Implementation: `insforge/functions/org-remove-member`.
 * @property {() => Promise<FunctionsResult>} orgSwitchCurrent
 *   Returns the caller's full org context: every membership + the org
 *   row, plus a `default_organization_id` (owner-org first). The active
 *   org is tracked client-side from this response.
 *   Implementation: `insforge/functions/org-switch-current`.
 * @property {(args: { eventId: string, reason?: string }) => Promise<FunctionsResult>} triggerEventCancellation
 *   Organizer-initiated mass cancellation of an event. The handler flips
 *   `events.status` to `cancelled`, issues a Stripe refund for every
 *   active ticket with `refund_application_fee: true` (full refund
 *   INCLUDING the service fee — the cancellation is the organizer's
 *   fault), and emails every unique buyer about the cancellation.
 *   Idempotent: an already-cancelled event returns 409 without
 *   double-refunding.
 *   Implementation: `insforge/functions/trigger-event-cancellation`.
 * @property {(args: { name: string, scopes: string[] }) => Promise<FunctionsResult>} createProviderApiKey
 *   Provider-only call that mints a fresh REST API key. The full secret
 *   is returned ONCE in the response payload; the database only ever
 *   stores the SHA-256 hash and a short visible prefix. Issue #201.
 *   Implementation: `insforge/functions/provider-api-key-create`.
 * @property {(args: { keyId: string }) => Promise<FunctionsResult>} revokeProviderApiKey
 *   Provider-only call that revokes a single API key by id. Idempotent.
 *   Implementation: `insforge/functions/provider-api-key-revoke`.
 * @property {(args: { eventId?: string, organizationSlug?: string, eventSlug?: string }) => Promise<FunctionsResult>} getEmbedPolicy
 *   Public, no-auth lookup that returns the CSP `frame-ancestors`
 *   whitelist for an event's embeddable widget. Callers pass either an
 *   event_id or the (organization_slug, event_slug) pair. Used by the
 *   `/embed/event/:slug` page to enforce the parent-domain check.
 *   Implementation: `insforge/functions/embed-policy`. Issue #195.
 * @property {(args: { organizationId: string, embedAllowedDomains: string[] }) => Promise<FunctionsResult>} orgUpdateEmbedDomains
 *   Owner/editor updates the org's `embed_allowed_domains` whitelist.
 *   The server normalises + dedupes; the response echoes the stored
 *   list. Implementation: `insforge/functions/org-update-embed-domains`.
 *   Issue #195.
 */

/** @type {FunctionsSurface} */
export const functions = {
  createTicketCheckout: ({
    ticketTypeId,
    quantity,
    buyerEmailHint,
    embedReferrerHost,
  }) =>
    invokeFunction("stripe-create-ticket-checkout", {
      body: {
        ticket_type_id: ticketTypeId,
        quantity,
        ...(buyerEmailHint ? { buyer_email_hint: buyerEmailHint } : {}),
        ...(embedReferrerHost
          ? { embed_referrer_host: embedReferrerHost }
          : {}),
      },
    }),

  connectOnboard: ({ returnUrl, refreshUrl }) =>
    invokeFunction("stripe-connect-onboard", {
      body: { return_url: returnUrl, refresh_url: refreshUrl },
    }),

  getTicketsByToken: ({ token }) =>
    invokeFunction("get-tickets-by-token", {
      body: { token },
    }),

  requestTicketRecovery: ({ email }) =>
    invokeFunction("request-ticket-recovery", {
      body: { email },
    }),

  validateBrandCss: ({ css, eventId }) =>
    invokeFunction("validate-brand-css", {
      body: { css, event_id: eventId },
    }),

  refundTicket: ({ ticketId, reason, recoveryToken }) =>
    invokeFunction("refund-ticket", {
      body: {
        ticket_id: ticketId,
        ...(reason ? { reason } : {}),
        ...(recoveryToken ? { recovery_token: recoveryToken } : {}),
      },
    }),

  requestRgpd: ({ email, type, description }) =>
    invokeFunction("rgpd-request", {
      body: {
        email,
        type,
        ...(description ? { description } : {}),
      },
    }),

  claimGuestTickets: () =>
    invokeFunction("claim-guest-tickets", { body: {} }),

  requestDemo: ({ name, email, org, message }) =>
    invokeFunction("request-demo", {
      body: {
        name,
        email,
        ...(org ? { org } : {}),
        ...(message ? { message } : {}),
      },
    }),

  sendEventNotification: ({ eventId, subject, body }) =>
    invokeFunction("send-event-notification", {
      body: { event_id: eventId, subject, body },
    }),

  triggerEventCancellation: ({ eventId, reason }) =>
    invokeFunction("trigger-event-cancellation", {
      body: {
        event_id: eventId,
        ...(reason ? { reason } : {}),
      },
    }),

  orgCreate: ({ slug, name, brandingJson }) =>
    invokeFunction("org-create", {
      body: {
        slug,
        name,
        ...(brandingJson ? { branding_json: brandingJson } : {}),
      },
    }),

  orgUpdateBranding: ({ organizationId, brandingJson }) =>
    invokeFunction("org-update-branding", {
      body: { organization_id: organizationId, branding_json: brandingJson },
    }),

  orgAddMember: ({ organizationId, email, role }) =>
    invokeFunction("org-add-member", {
      body: { organization_id: organizationId, email, role },
    }),

  orgRemoveMember: ({ organizationId, userId }) =>
    invokeFunction("org-remove-member", {
      body: { organization_id: organizationId, user_id: userId },
    }),

  orgSwitchCurrent: () =>
    invokeFunction("org-switch-current", { body: {} }),

  createProviderApiKey: ({ name, scopes }) =>
    invokeFunction("provider-api-key-create", {
      body: { name, scopes },
    }),

  revokeProviderApiKey: ({ keyId }) =>
    invokeFunction("provider-api-key-revoke", {
      body: { key_id: keyId },
    }),

  getEmbedPolicy: ({ eventId, organizationSlug, eventSlug } = {}) =>
    invokeFunction("embed-policy", {
      body: {
        ...(eventId ? { event_id: eventId } : {}),
        ...(organizationSlug ? { organization_slug: organizationSlug } : {}),
        ...(eventSlug ? { event_slug: eventSlug } : {}),
      },
    }),

  orgUpdateEmbedDomains: ({ organizationId, embedAllowedDomains }) =>
    invokeFunction("org-update-embed-domains", {
      body: {
        organization_id: organizationId,
        embed_allowed_domains: embedAllowedDomains,
      },
    }),
};

const STRIPE_CHECKOUT_PREFIX = "https://checkout.stripe.com/c/pay/";
const STRIPE_CONNECT_ONBOARDING_PREFIXES = [
  "https://connect.stripe.com/setup/e/",
  "https://connect.stripe.com/setup/s/",
  "https://connect.stripe.com/express/onboarding/",
];

function buildInvalidUrlError() {
  const err = /** @type {Error & { code: string }} */ (
    new Error("invalid_stripe_url")
  );
  err.code = "invalid_stripe_url";
  return err;
}

export function assertStripeCheckoutUrl(url) {
  if (typeof url !== "string" || !url.startsWith(STRIPE_CHECKOUT_PREFIX)) {
    throw buildInvalidUrlError();
  }
  return url;
}

export function assertStripeConnectOnboardingUrl(url) {
  if (
    typeof url !== "string" ||
    !STRIPE_CONNECT_ONBOARDING_PREFIXES.some((p) => url.startsWith(p))
  ) {
    throw buildInvalidUrlError();
  }
  return url;
}
